import React from "react";
import { useMemo } from "react";
import { useEnhancementConfig } from "@/hooks/useEnhancementConfig";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";

const HIDDEN_TOGGLE_KEYS = new Set([
  "advantage_plus_creative",
  "standard_enhancements_catalog",
]);

interface EnhancementTogglesPanelProps {
  values: Record<string, boolean>;
  onChange: (key: string, value: boolean) => void;
  onBatchChange?: (updates: Record<string, boolean>) => void;
  isLoading?: boolean;
}

export function EnhancementTogglesPanel({
  values,
  onChange,
  onBatchChange,
  isLoading: externalLoading,
}: EnhancementTogglesPanelProps) {
  const { data: config, isLoading: configLoading, isError } = useEnhancementConfig();

  const loading = externalLoading || configLoading;

  const groupedFormats = useMemo(() => {
    if (!config) return [];

    const formatMap = new Map<
      string,
      {
        format_key: string;
        label: string;
        description: string | null;
        sort_order: number;
        toggles: { toggle_key: string; label: string; description: string | null; default_enabled: boolean; sort_order: number }[];
      }
    >();

    for (const f of config.formats) {
      formatMap.set(f.format_key, {
        ...f,
        toggles: [],
      });
    }

    for (const t of config.toggles) {
      if (HIDDEN_TOGGLE_KEYS.has(t.toggle_key)) continue;
      const group = formatMap.get(t.format_key);
      if (group) {
        group.toggles.push(t);
      }
    }

    return Array.from(formatMap.values()).sort((a, b) => a.sort_order - b.sort_order);
  }, [config]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Enhancement configuration could not be loaded. Please refresh the page.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {groupedFormats.map((format) => {
        const isFlexible = format.format_key === "flexible";
        const visibleToggles = format.toggles;
        const allEnabled =
          visibleToggles.length > 0 &&
          visibleToggles.every((t) => values[t.toggle_key] === true);

        const handleMasterToggle = (checked: boolean) => {
          if (onBatchChange) {
            const updates: Record<string, boolean> = {};
            for (const t of visibleToggles) {
              updates[t.toggle_key] = checked;
            }
            onBatchChange(updates);
          } else {
            for (const t of visibleToggles) {
              onChange(t.toggle_key, checked);
            }
          }
        };

        return (
          <Card key={format.format_key}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">
                  {format.label}
                </CardTitle>
                {!isFlexible && visibleToggles.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`master-${format.format_key}`}
                      className="text-xs text-muted-foreground"
                    >
                      Enable all
                    </Label>
                    <Switch
                      id={`master-${format.format_key}`}
                      checked={allEnabled}
                      onCheckedChange={handleMasterToggle}
                    />
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {visibleToggles.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No enhancements available for this format.
                </p>
              ) : (
                visibleToggles.map((toggle) => (
                  <div
                    key={toggle.toggle_key}
                    className="flex items-start justify-between gap-3"
                  >
                    <div className="space-y-0.5 flex-1 min-w-0">
                      <Label
                        htmlFor={`toggle-${toggle.toggle_key}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {toggle.label}
                      </Label>
                      {isFlexible && toggle.description && (
                        <p className="text-xs text-muted-foreground">
                          {toggle.description}
                        </p>
                      )}
                    </div>
                    <Switch
                      id={`toggle-${toggle.toggle_key}`}
                      checked={values[toggle.toggle_key] === true}
                      onCheckedChange={(checked) =>
                        onChange(toggle.toggle_key, checked)
                      }
                    />
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
